import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navigation from '../components/Navigation'
import { useAuth } from '../context/AuthContext'
import { apiCall, getApiBaseUrl } from '../utils/api'
import { useToast } from '../context/ToastContext'
import { formatUSD } from '../utils/currency'
import StripeCheckoutModal from '../components/modals/StripeCheckoutModal'
import NewJobModal from '../components/modals/NewJobModal'
import UploadContentModal from '../components/modals/UploadContentModal'
import PricingPackagesModal from '../components/modals/PricingPackagesModal'
import ApiKeyManagement from '../components/ApiKeyManagement'
import { CREDIT_PACKAGES } from '../components/pricing/PricingPackages'

export default function DashboardPage() {
  const { user, loading, authToken, refreshUser } = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [dashboardData, setDashboardData] = useState(null)
  const [content, setContent] = useState([])
  const [jobs, setJobs] = useState([])
  const [billingHistory, setBillingHistory] = useState([])
  const [loadingData, setLoadingData] = useState(true)
  const [showCheckoutModal, setShowCheckoutModal] = useState(false)
  const [showNewJobModal, setShowNewJobModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const [showPricingPackagesModal, setShowPricingPackagesModal] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState(null)

  useEffect(() => {
    if (!loading && !user) {
      navigate('/')
      return
    }

    if (user && authToken) {
      loadDashboardData()
    }
  }, [user, loading, authToken, navigate])

  const loadDashboardData = async () => {
    try {
      setLoadingData(true)
      const [userResponse, statsResponse] = await Promise.all([
        apiCall('/v1/user/me'),
        apiCall('/v1/user/billing-stats'),
      ])

      if (userResponse.ok && statsResponse.ok) {
        const userData = await userResponse.json()
        const statsData = await statsResponse.json()

        setDashboardData({
          user: userData,
          stats: statsData,
        })

        // Also update AuthContext with fresh user data to keep localStorage in sync
        localStorage.setItem('user', JSON.stringify(userData))
      } else {
        console.error('Failed to load dashboard data:', userResponse.status, statsResponse.status)
        showToast('error', 'Failed to load dashboard data', 'Error')
      }

      // Load content and jobs and billing history
      await Promise.all([
        loadMyContent(),
        loadRecentJobs(),
        loadBillingHistory()
      ])
    } catch (error) {
      console.error('Error loading dashboard:', error)
      showToast('error', 'Failed to load dashboard data', 'Error')
    } finally {
      setLoadingData(false)
    }
  }

  const loadMyContent = async () => {
    try {
      const response = await apiCall('/v1/content')
      if (response.ok) {
        const data = await response.json()
        setContent(data.content || [])
      } else {
        setContent([])
      }
    } catch (error) {
      console.error('Error loading content:', error)
      setContent([])
    }
  }

  const loadRecentJobs = async () => {
    try {
      const response = await apiCall('/v1/jobs?limit=5')
      if (response.ok) {
        const data = await response.json()
        setJobs(data.jobs || [])
      } else {
        setJobs([])
      }
    } catch (error) {
      console.error('Error loading jobs:', error)
      setJobs([])
    }
  }

  const loadBillingHistory = async () => {
    try {
      const response = await apiCall('/v1/user/billing-history?limit=5&type=credit')
      if (response.ok) {
        const data = await response.json()
        setBillingHistory(data.transactions || [])
      } else {
        setBillingHistory([])
      }
    } catch (error) {
      console.error('Error loading billing history:', error)
      setBillingHistory([])
    }
  }

  const handleBuyCredits = async (pkg) => {
    if (!pkg) return

    try {
      const apiUrl = getApiBaseUrl()

      // Call API to create Stripe Checkout session
      const response = await fetch(`${apiUrl}/v1/payment/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          credit_package_id: pkg.id,
          success_url: `${window.location.origin}/payment/success`,
          cancel_url: `${window.location.origin}/payment/cancel`,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create checkout session')
      }

      const data = await response.json()

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        showToast('error', 'Checkout URL not provided', 'Error')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      showToast('error', error.message, 'Checkout Failed')
    }
  }

  const handleQuickAction = (action) => {
    switch (action) {
      case 'create-job':
        setShowNewJobModal(true)
        break
      case 'upload':
        setShowUploadModal(true)
        break
      case 'analytics':
        showToast('info', 'Analytics feature coming soon!', 'Coming Soon')
        break
      case 'api-keys':
        setShowApiKeyModal(true)
        break
      default:
        break
    }
  }

  const handleDeleteContent = async (contentId) => {
    if (!confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
      return
    }

    try {
      await apiCall(`/v1/content/${contentId}`, { method: 'DELETE' })
      showToast('success', 'Content deleted successfully', 'Deleted')
      loadMyContent()
    } catch (error) {
      console.error('Delete error:', error)
      showToast('error', 'Failed to delete content', 'Error')
    }
  }

  const handleDownloadContent = (contentId) => {
    const apiUrl = getApiBaseUrl()
    showToast('info', 'Starting download...', 'Download')
    window.open(`${apiUrl}/v1/content/${contentId}/download`, '_blank')
  }

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'upload': return 'fas fa-file-upload text-blue-600'
      case 'youtube': return 'fab fa-youtube text-red-600'
      case 'url': return 'fas fa-link text-primary-600'
      default: return 'fas fa-file text-gray-600'
    }
  }

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'upload': return 'bg-blue-100 dark:bg-blue-900/30'
      case 'youtube': return 'bg-red-100 dark:bg-red-900/30'
      case 'url': return 'bg-primary-100 dark:bg-primary-900/30'
      default: return 'bg-gray-100 dark:bg-gray-900/30'
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 dark:text-green-400'
      case 'processing': case 'queued': case 'running': return 'text-yellow-600 dark:text-yellow-400'
      case 'failed': return 'text-red-600 dark:text-red-400'
      default: return 'text-gray-600 dark:text-gray-400'
    }
  }

  // Redirect to home if not authenticated
  if (!loading && !user) {
    return null
  }

  const stats = [
    { icon: '📊', label: 'Total Jobs', value: jobs.length.toString(), color: 'blue' },
    { icon: '✅', label: 'Completed', value: jobs.filter(j => j.status === 'completed').length.toString(), color: 'green' },
    { icon: '⏳', label: 'Processing', value: jobs.filter(j => ['processing', 'queued', 'running'].includes(j.status)).length.toString(), color: 'yellow' },
    { icon: '💸', label: 'This Month', value: formatUSD(dashboardData?.stats?.total_spent_usd), color: 'purple' },
  ]

  const quickActions = [
    { icon: '➕', label: 'Create Job', action: 'create-job' },
    { icon: '📤', label: 'Upload Content', action: 'upload' },
    { icon: '📈', label: 'Analytics', action: 'analytics' },
    { icon: '🔑', label: 'API Keys', action: 'api-keys' },
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section with Credit Balance */}
        <div className="bg-primary-600 text-white rounded-xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Welcome to ContentIndexer</h1>
              <p className="text-primary-100">Manage your AI-powered content processing jobs and billing</p>
              <div className="mt-4 flex items-center gap-2 text-sm">
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">{dashboardData?.user?.email || user?.email}</span>
                <span className="bg-green-500 bg-opacity-60 px-3 py-1 rounded-full font-semibold">✓ Active</span>
              </div>
            </div>
            <div className="text-right bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 min-w-[250px]">
              <div className="text-4xl font-bold mb-2">
                {loadingData ? (
                  <span className="animate-pulse">$---.--</span>
                ) : (
                  formatUSD(dashboardData?.user?.account_balance_usd)
                )}
              </div>
              <div className="text-sm opacity-90 mb-4">Credit Balance</div>
              <button
                onClick={() => setShowPricingPackagesModal(true)}
                className="w-full bg-white text-primary-600 hover:bg-gray-100 text-sm font-semibold py-2 rounded-lg transition-colors"
              >
                💳 Buy Credits
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' : stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' : stat.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-primary-100 dark:bg-primary-900/30'}`}>
                  <div className="text-3xl">{stat.icon}</div>
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Purchase Section */}
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-md mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <div className="text-4xl mr-4">💰</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Quick Credit Top-Up</h3>
                <p className="text-gray-600 dark:text-gray-400">Get bonus credits with larger packages!</p>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              {CREDIT_PACKAGES.slice(1, 3).map(pkg => (
                <button
                  key={pkg.id}
                  onClick={() => handleBuyCredits(pkg)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    pkg.popular
                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                      : pkg.bestValue
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  ${pkg.price} → ${pkg.credits} <span className="text-xs opacity-80">(+{Math.round((pkg.bonus / pkg.price) * 100)}%)</span>
                </button>
              ))}
              <button
                onClick={() => setShowPricingPackagesModal(true)}
                className="border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                View All Packages
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {quickActions.map((action) => (
              <button
                key={action.action}
                onClick={() => handleQuickAction(action.action)}
                className="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group"
              >
                <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">{action.icon}</div>
                <div className="font-semibold text-gray-900 dark:text-white text-sm text-center">{action.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* My Content Section */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">My Content</h2>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              <i className="fas fa-plus mr-2"></i>Add Content
            </button>
          </div>
          {content.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <i className="fas fa-folder-open text-4xl mb-4 text-gray-300 dark:text-gray-600"></i>
              <p className="text-lg font-semibold mb-2">No content yet</p>
              <p className="text-sm">Upload files or add URL references to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {content.map((item) => (
                <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className={`p-3 rounded-lg ${getContentTypeColor(item.content_type)} mr-4`}>
                        <i className={`${getContentTypeIcon(item.content_type)} text-xl`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 dark:text-white truncate">
                          {item.file_name || 'Untitled'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {item.canonical_url}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            <i className="fas fa-tag mr-1"></i>
                            {item.content_type}
                          </span>
                          {item.file_size && (
                            <span>
                              <i className="fas fa-file mr-1"></i>
                              {formatFileSize(item.file_size)}
                            </span>
                          )}
                          <span>
                            <i className="fas fa-clock mr-1"></i>
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      {item.content_type === 'upload' && (
                        <button
                          onClick={() => handleDownloadContent(item.id)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 px-3 py-1 rounded text-sm transition-colors"
                          title="Download"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteContent(item.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 px-3 py-1 rounded text-sm transition-colors"
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Jobs */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Recent Jobs</h2>
            <button className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium">
              View All <i className="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
          {jobs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <i className="fas fa-folder-open text-4xl mb-4 text-gray-300 dark:text-gray-600"></i>
              <p className="text-lg font-semibold mb-2">No jobs yet</p>
              <p className="text-sm">Create your first job to start processing content</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow">
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {job.id.slice(0, 8)}...
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(job.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium capitalize ${getStatusColor(job.status)}`}>
                      {job.status}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                      {job.content_url?.slice(0, 30)}...
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Billing & Usage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Current Usage */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Current Month Usage</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Dollars Spent</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {formatUSD(dashboardData?.stats?.total_spent_usd)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Dollars Saved</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {formatUSD(dashboardData?.stats?.total_bonus_credits_usd)}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Savings from bonus credits on credit purchases
              </div>
            </div>
          </div>

          {/* Billing History */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Recent Transactions</h2>
            {billingHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <i className="fas fa-receipt text-4xl mb-4 text-gray-300 dark:text-gray-600"></i>
                <p className="text-lg font-semibold mb-2">No transactions yet</p>
                <p className="text-sm">Purchase credits to get started with ContentIndexer</p>
              </div>
            ) : (
              <div className="space-y-3">
                {billingHistory.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-2">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{tx.description}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className={`text-sm font-semibold ${tx.amount_usd >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {tx.amount_usd >= 0 ? '+' : ''}{formatUSD(Math.abs(tx.amount_usd))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCheckoutModal && (
        <StripeCheckoutModal
          onClose={() => {
            setShowCheckoutModal(false)
            setSelectedPackage(null)
            loadDashboardData()
          }}
          package={selectedPackage}
        />
      )}

      {showNewJobModal && (
        <NewJobModal
          onClose={() => setShowNewJobModal(false)}
          onJobCreated={() => {
            loadRecentJobs()
          }}
        />
      )}

      {showUploadModal && (
        <UploadContentModal
          onClose={() => setShowUploadModal(false)}
          onContentAdded={() => {
            loadMyContent()
          }}
        />
      )}

      {showApiKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">API Key Management</h2>
              <button
                onClick={() => setShowApiKeyModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-light leading-none"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <ApiKeyManagement />
          </div>
        </div>
      )}

      {showPricingPackagesModal && (
        <PricingPackagesModal
          onClose={async () => {
            setShowPricingPackagesModal(false)
            // Refresh both AuthContext user and dashboard data
            await refreshUser()
            loadDashboardData()
          }}
        />
      )}
    </div>
  )
}
